import React, { useMemo } from 'react';
import { observer } from 'mobx-react';

import { styled } from '@this/constants/themes';
import type Trip from '@this/domain/trip/trip2';
import { JsonViewer } from '@this/components/arrangement/virtual_counter/virtual_counter_aside/json_viewer';
import type HotelElement from '@this/domain/hotel_element2';
import { getColor } from '@this/shared/ui/theme';

// elementsの実際の型は (ElementBaseInterface | ElementBase)[] なので、
// 共通プロパティのみを持つ型を使用
const isHotelElementWithRawJson = (element: { type: string }): element is HotelElement => {
  return element.type === 'hotel' && !!(element as HotelElement).rawJson;
};

type Props = {
  className?: string;
  trip: Trip;
};

const getHotelElementList = (trip: Trip, provider: 'mynavi' | 'agoda') =>
  trip.order?.orderItems.reduce((acc, item) => {
    item.hotels.filter(e => e.provider === provider).forEach(e => acc.push(e));
    return acc;
  }, [] as HotelElement[]) || [];

export const HotelInfo = observer(({ className, trip }: Props) => {
  const agodaHotels = useMemo(() => getHotelElementList(trip, 'agoda'), [trip]);
  const mynaviHotels = useMemo(() => getHotelElementList(trip, 'mynavi'), [trip]);

  return (
    <div className={className}>
      <Section>
        <h3>ホテル情報</h3>
        <p>Expedia連絡先: 03-6743-8536</p>
        <p>Expedia旅程番号: {trip.itineraryId ?? '-'}</p>
        {trip.order?.orderItems &&
          trip.order.orderItems.map(item =>
            item.elements.filter(isHotelElementWithRawJson).map(hotelElement => (
              <div key={hotelElement.id}>
                <JsonViewer json={hotelElement.rawJson} label="ホテル詳細" />
              </div>
            ))
          )}
      </Section>

      {agodaHotels.length > 0 && (
        <Section>
          <h3>agodaホテル</h3>
          {agodaHotels.map(h => (
            <div key={h.id}>
              <p>▼{h.name}</p>
              <p>予約番号: {h.hotelReservationNumber}</p>
            </div>
          ))}
        </Section>
      )}

      {mynaviHotels.length > 0 && (
        <Section>
          <h3>マイナビホテル</h3>
          {mynaviHotels.map(h => (
            <div key={h.id}>
              <p>▼{h.name}</p>
              <p>予約番号: {h.hotelReservationNumber}</p>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
});

const Section = styled.section`
  & + & {
    border: 1px solid ${getColor('border', 'divider')};
  }
`;
